import { getLocale } from '../i18n';

/** Map an app locale to the BCP-47 tag used for Intl formatting. */
const INTL_LOCALE: Record<string, string> = {
  en: 'en-IE',
  cs: 'cs-CZ',
};

function intlLocale(): string {
  return INTL_LOCALE[getLocale()] ?? 'en-IE';
}

export function formatEuros(cents: number | null | undefined): string {
  const c = cents ?? 0;
  return new Intl.NumberFormat(intlLocale(), {
    style: 'currency',
    currency: 'EUR',
  }).format(c / 100);
}

export function formatDate(iso: string | Date | null | undefined): string {
  if (!iso) return '';
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  return new Intl.DateTimeFormat(intlLocale(), {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d);
}

/** Calendar day without the time — "member since", ledger and receipt dates. */
export function formatDay(iso: string | Date | null | undefined): string {
  if (!iso) return '';
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat(intlLocale(), { year: 'numeric', month: 'short', day: 'numeric' }).format(d);
}

export interface StatusBadgeInfo {
  /** i18n key suffix under `common.orderStatus.*`; also the raw status code. */
  key: string;
  /** English fallback label (used when no translation is registered). */
  label: string;
  classes: string;
  /** In-flight statuses show a pulsing dot. */
  pulse: boolean;
}

// Flat chips (app.css): mint when delivered, quiet + pulsing dot while in flight,
// error text on the quiet chip for failed / refunded.
const CHIP_ERROR = 'chip !text-error';

const STATUS_MAP: Record<string, StatusBadgeInfo> = {
  CREATED:   { key: 'CREATED',    label: 'Created',    classes: 'chip', pulse: false },
  PAID:      { key: 'PAID',       label: 'Paid',       classes: 'chip', pulse: true },
  PROCESSING:{ key: 'PROCESSING', label: 'Processing', classes: 'chip', pulse: true },
  DELIVERED: { key: 'DELIVERED',  label: 'Delivered',  classes: 'chip-mint', pulse: false },
  COMPLETED: { key: 'COMPLETED',  label: 'Completed',  classes: 'chip-mint', pulse: false },
  FAILED:    { key: 'FAILED',     label: 'Failed',     classes: CHIP_ERROR, pulse: false },
  REFUNDED:  { key: 'REFUNDED',   label: 'Refunded',   classes: CHIP_ERROR, pulse: false },
};

export function statusBadge(status: string): StatusBadgeInfo {
  return STATUS_MAP[status] ?? { key: status, label: status, classes: 'chip', pulse: false };
}

export const TERMINAL_STATUSES = new Set(['COMPLETED', 'FAILED', 'REFUNDED']);
